import { type FC, Suspense } from 'react';
import {
    graphql,
    useFragment,
    useRelayEnvironment,
    useLazyLoadQuery,
    usePaginationFragment,
} from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { flattenConnectionNodes } from 'dibs-ts-utils/exports/flattenConnectionNodes';
import ItemTilesCarousel from 'dibs-product-tile-carousel/exports/ItemTilesCarousel';
import authModalLoader from 'dibs-buyer-layout/exports/authModalLoader';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';

import SharedFavoritesProvider from '../../sharedComponents/SharedFavoritesProvider';
import { SharedHeart } from '../../sharedComponents/SharedHeart';
import { useHomePageContext } from '../HomePageContext';
import { VIDEO_MODULE_TRIGGER } from './videoComponentTrackingHelpers';

import { type HpSharedVideoComponentCarousel_componentModule$key } from './__generated__/HpSharedVideoComponentCarousel_componentModule.graphql';
import { type HpSharedVideoComponentCarouselQuery as HpSharedVideoComponentCarouselQueryType } from './__generated__/HpSharedVideoComponentCarouselQuery.graphql';
import { type HpSharedVideoComponentCarousel_viewer$key } from './__generated__/HpSharedVideoComponentCarousel_viewer.graphql';
import { type HpSharedVideoComponentCarousel_user$key } from './__generated__/HpSharedVideoComponentCarousel_user.graphql';

const CAROUSEL_ITEMS_TO_SHOW_RESP = 4;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;
const CAROUSEL_CAPACITY = 12;

const componentModuleFragment = graphql`
    fragment HpSharedVideoComponentCarousel_componentModule on VideoModule {
        ctaLink
    }
`;

const carouselItemsQuery = graphql`
    query HpSharedVideoComponentCarouselQuery(
        $uriRef: String!
        $userCountryCode: String
        $priceBookName: String
        $isTrade: Boolean
        $userId: String
    ) {
        viewer {
            ...HpSharedVideoComponentCarousel_viewer @arguments(uriRef: $uriRef)
        }
    }
`;

const viewerFragment = graphql`
    fragment HpSharedVideoComponentCarousel_viewer on Viewer
    @refetchable(queryName: "HpSharedVideoComponentCarouselPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 5 }
        cursor: { type: "String" }
        uriRef: { type: "String!" }
    ) {
        itemSearchItems(
            first: $count
            after: $cursor
            uriRef: $uriRef
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) @connection(key: "HpSharedVideoComponentCarousel_itemSearchItems", filters: []) {
            # eslint-disable-next-line relay/unused-fields
            edges {
                node {
                    serviceId
                    ...ItemTilesCarousel_items
                        @arguments(
                            fetchFavorites: false
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                    ...SharedHeart_item
                }
            }
            totalResults
        }
        ...ItemTilesCarousel_viewer @arguments(fetchFavorites: false, userId: $userId)
        ...SharedFavoritesProvider_viewer
    }
`;

const userFragment = graphql`
    fragment HpSharedVideoComponentCarousel_user on User {
        serviceId
        ...ItemTilesCarousel_user
    }
`;

type CarouselContentProps = {
    user?: HpSharedVideoComponentCarousel_user$key | null;
    fetchFavorites: boolean;
    uriRef: string;
};

const CarouselContent: FC<CarouselContentProps> = ({ uriRef, fetchFavorites, user: userRef }) => {
    const environment = useRelayEnvironment();
    const { isMobile } = useHomePageContext();
    const { currency } = useCurrency();

    const data = useLazyLoadQuery<HpSharedVideoComponentCarouselQueryType>(carouselItemsQuery, {
        uriRef,
    });

    const { data: viewer, loadNext } = usePaginationFragment<
        HpSharedVideoComponentCarouselQueryType,
        HpSharedVideoComponentCarousel_viewer$key
    >(viewerFragment, data.viewer);

    const user = useFragment(userFragment, userRef ?? null);
    const items = flattenConnectionNodes(viewer?.itemSearchItems);
    const totalResults = viewer?.itemSearchItems?.totalResults || 0;
    const userId = user?.serviceId;

    if (!items.length) {
        return null;
    }

    return (
        <SharedFavoritesProvider
            viewer={viewer}
            userId={userId}
            itemIds={items.map(item => item?.serviceId)}
            fetchFavorites={fetchFavorites}
            environment={environment}
        >
            {({ favorites }: $TSFixMe) => (
                <ItemTilesCarousel
                    disableContentPadding
                    viewer={viewer}
                    user={user}
                    items={items}
                    currency={currency}
                    authModalLoader={authModalLoader}
                    carouselCapacity={CAROUSEL_CAPACITY}
                    totalResults={totalResults}
                    loadNext={loadNext}
                    trackingList={VIDEO_MODULE_TRIGGER}
                    itemsToShow={
                        isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP
                    }
                    tileImageSize={isMobile ? 'imageLarge' : 'imageMedium'}
                    isInfinite={!isMobile}
                    showArrows={!isMobile}
                    showQuickView={false}
                    hideImagePadding
                    gapSize="small"
                    fullWidth={!!isMobile}
                    favoritesComponentOverrides={items.map(item => () => (
                        <SharedHeart
                            item={item}
                            trackingList={`homepage - ${VIDEO_MODULE_TRIGGER}`}
                            favorites={favorites}
                        />
                    ))}
                    tileClassName={classnames(dibsCss.bgWhite, dibsCss.pSmall, dibsCss.pb0)}
                />
            )}
        </SharedFavoritesProvider>
    );
};

type Props = {
    componentModule: HpSharedVideoComponentCarousel_componentModule$key;
    user?: HpSharedVideoComponentCarousel_user$key | null;
    fetchFavorites: boolean;
};

const getSellerStorefrontLink = (ctaLink?: string | null): string | null => {
    if (!ctaLink) {
        return null;
    }
    const suffix = ctaLink.endsWith('/') ? 'shop/' : '/shop/';
    return `${ctaLink}${suffix}?sort=recommended`;
};

const HpSharedVideoComponentCarousel: FC<Props> = ({
    fetchFavorites,
    componentModule: componentModuleRef,
    user,
}) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);
    const uriRef = getSellerStorefrontLink(componentModule?.ctaLink);

    if (!uriRef) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <CarouselContent uriRef={uriRef} fetchFavorites={fetchFavorites} user={user} />
        </Suspense>
    );
};

export default HpSharedVideoComponentCarousel;
